import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DollarSign,
  Percent,
  Plus,
  Trash2,
  Edit3,
  RefreshCw,
  Save,
  X,
  Plane,
  Building2,
  Car,
  FileText,
  Users,
  Calculator,
  AlertCircle,
  CheckCircle,
  Settings,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaymentMarkupCard } from './PaymentMarkupCard';
import { LowCostCarriersCard } from './LowCostCarriersCard';
import { UmrahPackagesAdminCard } from './UmrahPackagesAdminCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

export const MarkupPanel = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'percentage',
    value: '',
    service_type: 'all',
    passenger_type: 'all',
    is_active: true,
    priority: 0,
    min_base_price: '',
    max_base_price: ''
  });

  // Calculator state
  const [calcBasePrice, setCalcBasePrice] = useState('');
  const [calcService, setCalcService] = useState('all');
  const [calcPassenger, setCalcPassenger] = useState('all');
  const [calcResult, setCalcResult] = useState(null);
  
  // Manual markup inputs
  const [manualMarkupPercent, setManualMarkupPercent] = useState('');
  const [manualMarkupGBP, setManualMarkupGBP] = useState('');
  const [manualCalcResult, setManualCalcResult] = useState(null);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/markups`);
      if (response.data.success) {
        setRules(response.data.rules || []);
        setStats(response.data.stats || { total: 0, active: 0, inactive: 0 });
      }
    } catch (error) {
      console.error('Error fetching markup rules:', error);
      toast.error([401, 403].includes(error.response?.status) ? 'Admin login required to manage markups' : 'Failed to load markup rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.value) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        priority: parseInt(formData.priority) || 0,
        min_base_price: formData.min_base_price ? parseFloat(formData.min_base_price) : null,
        max_base_price: formData.max_base_price ? parseFloat(formData.max_base_price) : null
      };

      if (editingRule) {
        await axios.put(`${API_URL}/api/admin/markups/${editingRule.id}`, payload);
        toast.success('Markup rule updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/markups`, payload);
        toast.success('Markup rule created successfully');
      }

      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Error saving markup rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to save markup rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this markup rule?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/markups/${ruleId}`);
      toast.success('Markup rule deleted');
      fetchRules();
    } catch (error) {
      console.error('Error deleting markup rule:', error);
      toast.error('Failed to delete markup rule');
    }
  };

  const handleToggle = async (ruleId) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/markups/${ruleId}/toggle`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchRules();
      }
    } catch (error) {
      console.error('Error toggling markup rule:', error);
      toast.error('Failed to toggle markup rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      value: rule.value.toString(),
      service_type: rule.service_type,
      passenger_type: rule.passenger_type,
      is_active: rule.is_active,
      priority: rule.priority || 0,
      min_base_price: rule.min_base_price?.toString() || '',
      max_base_price: rule.max_base_price?.toString() || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rule_type: 'percentage',
      value: '',
      service_type: 'all',
      passenger_type: 'all',
      is_active: true,
      priority: 0,
      min_base_price: '',
      max_base_price: ''
    });
    setEditingRule(null);
    setShowForm(false);
  };

  const calculateMarkup = async () => {
    if (!calcBasePrice) {
      toast.error('Please enter a base price');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/markups/calculate`, {
        base_price: parseFloat(calcBasePrice),
        service_type: calcService,
        passenger_type: calcPassenger,
        passenger_count: 1
      });
      
      if (response.data.success) {
        setCalcResult(response.data);
      }
    } catch (error) {
      console.error('Error calculating markup:', error);
      toast.error('Failed to calculate markup');
    }
  };

  // Manual markup calculation
  const calculateManualMarkup = () => {
    if (!calcBasePrice) {
      toast.error('Please enter a base price');
      return;
    }
    
    const basePrice = parseFloat(calcBasePrice);
    let totalMarkup = 0;
    const appliedMarkups = [];
    
    // Calculate percentage markup
    if (manualMarkupPercent && parseFloat(manualMarkupPercent) > 0) {
      const percentMarkup = basePrice * (parseFloat(manualMarkupPercent) / 100);
      totalMarkup += percentMarkup;
      appliedMarkups.push({
        type: 'percentage',
        value: parseFloat(manualMarkupPercent),
        amount: percentMarkup
      });
    }
    
    // Calculate fixed GBP markup
    if (manualMarkupGBP && parseFloat(manualMarkupGBP) > 0) {
      const fixedMarkup = parseFloat(manualMarkupGBP);
      totalMarkup += fixedMarkup;
      appliedMarkups.push({
        type: 'fixed',
        value: fixedMarkup,
        amount: fixedMarkup
      });
    }
    
    const finalPrice = basePrice + totalMarkup;
    const effectivePercent = basePrice > 0 ? ((totalMarkup / basePrice) * 100).toFixed(2) : 0;
    
    setManualCalcResult({
      base_price: basePrice,
      total_markup: totalMarkup,
      final_price: finalPrice,
      effective_percentage: effectivePercent,
      applied_markups: appliedMarkups
    });
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'flights': return <Plane className="h-4 w-4" />;
      case 'hotels': return <Building2 className="h-4 w-4" />;
      case 'transport': return <Car className="h-4 w-4" />;
      case 'visa': return <FileText className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <PaymentMarkupCard />
      <LowCostCarriersCard />
      <UmrahPackagesAdminCard />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Rules</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Settings className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Active Rules</p>
                <p className="text-3xl font-bold">{stats.active}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Inactive Rules</p>
                <p className="text-3xl font-bold">{stats.inactive}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Profit Engine</p>
                <p className="text-xl font-bold">Active</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Markup Rule
        </Button>
        <Button variant="outline" onClick={fetchRules}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              {editingRule ? <Edit3 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editingRule ? 'Edit Markup Rule' : 'Create New Markup Rule'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Rule Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Rule Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Standard Flight Markup"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                {/* Rule Type */}
                <div className="space-y-2">
                  <Label>Markup Type *</Label>
                  <Select
                    value={formData.rule_type}
                    onValueChange={(v) => setFormData({ ...formData, rule_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">
                        <span className="flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          Percentage (%)
                        </span>
                      </SelectItem>
                      <SelectItem value="fixed">
                        <span className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Fixed Amount (£)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Value */}
                <div className="space-y-2">
                  <Label htmlFor="value">
                    Value * {formData.rule_type === 'percentage' ? '(%)' : '(£)'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="value"
                      type="number"
                      step="0.01"
                      placeholder={formData.rule_type === 'percentage' ? 'e.g., 8' : 'e.g., 50'}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="pl-8"
                      required
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {formData.rule_type === 'percentage' ? '%' : '£'}
                    </span>
                  </div>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority (Higher = First)</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  />
                </div>

                {/* Service Type */}
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      <SelectItem value="flights">Flights Only</SelectItem>
                      <SelectItem value="hotels">Hotels Only</SelectItem>
                      <SelectItem value="transport">Transport Only</SelectItem>
                      <SelectItem value="visa">Visa Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Passenger Type */}
                <div className="space-y-2">
                  <Label>Passenger Type</Label>
                  <Select
                    value={formData.passenger_type}
                    onValueChange={(v) => setFormData({ ...formData, passenger_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Passengers</SelectItem>
                      <SelectItem value="adult">Adults Only</SelectItem>
                      <SelectItem value="youth">Youth Only</SelectItem>
                      <SelectItem value="child">Children Only</SelectItem>
                      <SelectItem value="infant">Infants Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Min Price */}
                <div className="space-y-2">
                  <Label htmlFor="min_price">Min Base Price (£)</Label>
                  <Input
                    id="min_price"
                    type="number"
                    step="0.01"
                    placeholder="Optional"
                    value={formData.min_base_price}
                    onChange={(e) => setFormData({ ...formData, min_base_price: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Only apply if base price is at least this amount</p>
                </div>

                {/* Max Price */}
                <div className="space-y-2">
                  <Label htmlFor="max_price">Max Base Price (£)</Label>
                  <Input
                    id="max_price"
                    type="number"
                    step="0.01"
                    placeholder="Optional"
                    value={formData.max_base_price}
                    onChange={(e) => setFormData({ ...formData, max_base_price: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Only apply if base price is at most this amount</p>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  {formData.is_active ? (
                    <span className="text-green-600 font-medium">Active - This rule will be applied</span>
                  ) : (
                    <span className="text-gray-500">Inactive - This rule is disabled</span>
                  )}
                </Label>
              </div>

              {/* Form Actions */}
              <div className="flex items-center gap-3">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="h-4 w-4 mr-2" />
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Markup Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-purple-500" />
            Markup Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Calculate with existing rules */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Calculate with Existing Rules
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label>Base Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter base price"
                  value={calcBasePrice}
                  onChange={(e) => setCalcBasePrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Service</Label>
                <Select value={calcService} onValueChange={setCalcService}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    <SelectItem value="flights">Flights</SelectItem>
                    <SelectItem value="hotels">Hotels</SelectItem>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="visa">Visa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Passenger Type</Label>
                <Select value={calcPassenger} onValueChange={setCalcPassenger}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Passengers</SelectItem>
                    <SelectItem value="adult">Adult</SelectItem>
                    <SelectItem value="youth">Youth</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="infant">Infant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={calculateMarkup} className="bg-purple-600 hover:bg-purple-700">
                <Calculator className="h-4 w-4 mr-2" />
                Calculate
              </Button>
            </div>

            {calcResult && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Base Price</p>
                    <p className="text-xl font-bold">£{calcResult.base_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Markup</p>
                    <p className="text-xl font-bold text-green-600">+£{calcResult.total_markup.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Markup %</p>
                    <p className="text-xl font-bold text-purple-600">{calcResult.markup_percentage}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Final Price</p>
                    <p className="text-xl font-bold text-blue-600">£{calcResult.final_price.toFixed(2)}</p>
                  </div>
                </div>
                {calcResult.applied_rules.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Applied Rules:</p>
                    <div className="space-y-1">
                      {calcResult.applied_rules.map((rule, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span>{rule.rule_name}</span>
                          <span className="font-medium text-green-600">
                            +£{rule.markup_amount.toFixed(2)} 
                            ({rule.rule_type === 'percentage' ? `${rule.value}%` : `£${rule.value}`})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {calcResult.applied_rules.length === 0 && (
                  <p className="mt-3 text-sm text-gray-500">No markup rules applied for this configuration.</p>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or</span>
            </div>
          </div>

          {/* Manual Markup Entry */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Edit3 className="h-4 w-4" />
              Manual Markup Entry
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label>Base Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter base price"
                  value={calcBasePrice}
                  onChange={(e) => setCalcBasePrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Markup Percentage (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 10"
                    value={manualMarkupPercent}
                    onChange={(e) => setManualMarkupPercent(e.target.value)}
                    className="pr-8"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fixed Markup (£)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 50"
                    value={manualMarkupGBP}
                    onChange={(e) => setManualMarkupGBP(e.target.value)}
                    className="pl-6"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                </div>
              </div>
              <Button onClick={calculateManualMarkup} className="bg-green-600 hover:bg-green-700">
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Manual
              </Button>
            </div>

            {manualCalcResult && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Base Price</p>
                    <p className="text-xl font-bold">£{manualCalcResult.base_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Markup</p>
                    <p className="text-xl font-bold text-green-600">+£{manualCalcResult.total_markup.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Effective %</p>
                    <p className="text-xl font-bold text-purple-600">{manualCalcResult.effective_percentage}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Final Price</p>
                    <p className="text-xl font-bold text-blue-600">£{manualCalcResult.final_price.toFixed(2)}</p>
                  </div>
                </div>
                {manualCalcResult.applied_markups.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Breakdown:</p>
                    <div className="space-y-1">
                      {manualCalcResult.applied_markups.map((markup, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            {markup.type === 'percentage' ? (
                              <><Percent className="h-3 w-3 text-purple-500" /> Percentage Markup</>
                            ) : (
                              <><DollarSign className="h-3 w-3 text-green-500" /> Fixed Markup</>
                            )}
                          </span>
                          <span className="font-medium text-green-600">
                            +£{markup.amount.toFixed(2)} 
                            ({markup.type === 'percentage' ? `${markup.value}%` : `£${markup.value}`})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Markup Rules ({rules.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-4" />
              <p className="text-gray-500">Loading markup rules...</p>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <DollarSign className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No markup rules configured</p>
              <p className="text-sm">Click "Add Markup Rule" to create your first rule</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className={`p-4 border rounded-xl transition-all ${
                    rule.is_active 
                      ? 'border-green-200 bg-green-50/50' 
                      : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${
                        rule.rule_type === 'percentage' 
                          ? 'bg-purple-100 text-purple-600' 
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {rule.rule_type === 'percentage' 
                          ? <Percent className="h-5 w-5" /> 
                          : <DollarSign className="h-5 w-5" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{rule.name}</h3>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'} 
                            className={rule.is_active ? 'bg-green-500' : ''}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            {rule.rule_type === 'percentage' 
                              ? <><Percent className="h-3 w-3" /> {rule.value}%</>
                              : <><DollarSign className="h-3 w-3" /> £{rule.value}</>
                            }
                          </span>
                          <span className="flex items-center gap-1">
                            {getServiceIcon(rule.service_type)}
                            {rule.service_type === 'all' ? 'All Services' : rule.service_type}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {rule.passenger_type === 'all' ? 'All Passengers' : rule.passenger_type}
                          </span>
                          {rule.priority > 0 && (
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              Priority: {rule.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggle(rule.id)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(rule)}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(rule.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Price Range Indicator */}
                  {(rule.min_base_price || rule.max_base_price) && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        Price Range: 
                        {rule.min_base_price && ` £${rule.min_base_price}+`}
                        {rule.min_base_price && rule.max_base_price && ' to '}
                        {rule.max_base_price && ` up to £${rule.max_base_price}`}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-700">Markup Rules Information</p>
              <ul className="text-sm text-blue-600 mt-2 space-y-1 list-disc list-inside">
                <li>Rules with higher priority are applied first</li>
                <li>Multiple rules can stack (all applicable rules are applied)</li>
                <li>Percentage markups are calculated on the running total after fixed markups</li>
                <li>Price range filters let you apply different markups to different price tiers</li>
                <li>Disabled rules are never applied, even if conditions match</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarkupPanel;
